import { camelCase, isEmpty, startCase } from 'lodash';
import {
  AppliedFiltersData,
  AppliedSectionFilterItem,
  FiltersData,
  ListAppliedItem,
  ListFilterKeys,
  Mappings,
  MobileSiftScreen,
  SectionFilterInput,
  SectionFilterKeys,
  SectionInputItem,
  TreeFilterInput,
  TreeFilterKeys,
  TreeInputData,
} from '../filter/model';
import { createListFiltersData } from '../filter/helpers';
import {
  ThreadFilterResponse,
  ThreadStatusFilter,
  ThreadStatusFilterRequest,
  GroupData,
} from './model';

const traverseGroupData = (
  data: GroupData,
  root: string,
  appliedFilterIds: string[]
): TreeInputData[] => {
  return Object.entries(data || {}).map(([key, { count, children }]) => {
    return {
      id: `${key}:_${(Math.random() * 10000).toFixed(0)}`,
      value: `${key} (${count})`,
      selected: appliedFilterIds.includes(key),
      ...(children && Object.keys(children).length > 0
        ? { data: traverseGroupData(children, key, appliedFilterIds) }
        : {}),
    };
  });
};

export const handleStatusFilter = (
  status: ThreadStatusFilter,
  appliedStatus?: ThreadStatusFilterRequest
): SectionInputItem[] => {
  const applied: string[] = appliedStatus
    ? Object.entries(appliedStatus)
        .map(([key, value]) => (value as string[]).map((x) => `${key}-${x}`))
        .flatMap((el) => el)
    : [];

  const statusfilter: SectionInputItem[] = Object.entries(status)
    .map(([key, value]) => ({
      title: key,
      data: Object.entries(value).map(([status, count]) => ({
        id: `${key}-${status}`,
        value: `${startCase(camelCase(status))} (${count})`,
        selected: applied.includes(`${key}-${status}`),
      })),
    }))
    .filter(({ data }) => !isEmpty(data));
  return statusfilter;
};

interface ThreadsMapping extends Mappings<ThreadFilterResponse> {
  filterScreen: MobileSiftScreen[];
}

const getAppliedListFilterIds = (
  listFilters: Record<string, ListAppliedItem[]>,
  key: ListFilterKeys | TreeFilterKeys
): string[] => {
  const filters = listFilters?.[key];
  if (filters) {
    return filters.map(({ id }) => id);
  }
  return [];
};

const getAppliedSectionFilters = (
  appliedSectionFilter: Partial<AppliedSectionFilterItem>,
  key: SectionFilterKeys
): Record<string, string[]> => {
  return (appliedSectionFilter[key] || []).reduce(
    (acc, item) => {
      if (!acc[item.section]) {
        acc[item.section] = [];
      }
      acc[item.section].push(item.id);
      return acc;
    },
    {} as Record<string, string[]>
  );
};

const getAppliedTreeFilterIds = (
  listFilters: Record<string, ListAppliedItem[]>,
  key: ListFilterKeys | TreeFilterKeys
): string[] => {
  const filters = listFilters?.[key];
  if (filters) {
    return [...new Set(filters.flatMap(({ id }) => id.split('#')))];
  }
  return [];
};

export const getThreadFilterItems = (
  data: ThreadFilterResponse,
  screen: MobileSiftScreen,
  appliedFilters: AppliedFiltersData | undefined
): FiltersData => {
  const appliedListFilter = appliedFilters?.listFilter || {};
  const mappings: ThreadsMapping[] = [
    {
      dataKey: 'cfas',
      idKey: 'id',
      valueKey: 'name',
      filterKey: ListFilterKeys.Cfa,
      filterScreen: [MobileSiftScreen.CUSTOMER_THREADS],
    },
    {
      dataKey: 'members',
      idKey: 'inviteId',
      valueKey: 'employeeName',
      filterKey: ListFilterKeys.TeamMembers,
      filterScreen: [MobileSiftScreen.CUSTOMER_THREADS],
    },
    {
      dataKey: 'divisions',
      idKey: 'id',
      valueKey: 'name',
      filterKey: ListFilterKeys.Divisions,
      filterScreen: [MobileSiftScreen.CUSTOMER_THREADS],
    },
  ];

  const treeMappings = [
    {
      data: data?.routes,
      optionKey: TreeFilterKeys.Routes,
      root: 'Routes',
    },
    {
      data: data?.customerSegment,
      optionKey: TreeFilterKeys.Segments,
      root: 'Segments',
    },
    {
      data: data?.dynamicSegment,
      optionKey: TreeFilterKeys.DynamicSegments,
      root: 'DynamicSegments',
    },
    {
      data: data?.customerLifeCycle,
      optionKey: TreeFilterKeys.CustomerLifeCycle,
      root: 'CustomerLifeCycle',
    },
  ];

  const screenMappings = mappings
    .filter(({ filterScreen }) => filterScreen.includes(screen))
    .map(({ filterScreen, ...mapping }) => {
      const { filterKey } = mapping;
      const appliedFilterIds = getAppliedListFilterIds(
        appliedListFilter,
        filterKey
      );
      return {
        ...mapping,
        appliedFilterIds,
      } as Mappings<ThreadFilterResponse>;
    });
  const listFilter = createListFiltersData(data, screenMappings) || {};
  const sectionFilter: Partial<SectionFilterInput> = {};
  const treeFilter: Partial<TreeFilterInput> = {};

  const appliedSectionFilter = appliedFilters?.sectionFilter || {};
  const appliedStatus = getAppliedSectionFilters(
    appliedSectionFilter,
    SectionFilterKeys.Status
  );
  if (screen === MobileSiftScreen.CUSTOMER_THREADS) {
    const customerThreadsStatus = data?.statusFilter;
    const statusfilter = customerThreadsStatus
      ? (handleStatusFilter(customerThreadsStatus).map(({ title, data }) => ({
          title,
          data: data.map((item) => ({
            ...item,
            selected: appliedStatus[title]?.includes(item.id) || false,
          })),
        })) as SectionInputItem[])
      : [];
    const { customerStatus, type, whatsapp } = data;
    const otherFilters = { type, customerStatus, whatsapp };
    const appliedOther = getAppliedSectionFilters(
      appliedSectionFilter,
      SectionFilterKeys.Other
    );
    const otherFiltersData: SectionInputItem[] = Object.entries(
      otherFilters
    ).map(([key, value]) => ({
      title: startCase(camelCase(key)),
      data: Object.keys(value || {}).map((item) => ({
        id: item,
        value: item,
        selected: appliedOther[key]?.includes(item) || false,
      })),
    }));

    sectionFilter[SectionFilterKeys.Status] = {
      multiSelect: true,
      value: statusfilter,
    };
    sectionFilter[SectionFilterKeys.Other] = {
      multiSelect: true,
      value: otherFiltersData,
    };
    const appliedTreeFilter = appliedFilters?.treeFilter || {};
    treeMappings.forEach(({ data, optionKey, root }) => {
      if (data) {
        const appliedFilterIds = getAppliedTreeFilterIds(
          appliedTreeFilter,
          optionKey
        );
        const reqData = traverseGroupData(data, root, appliedFilterIds);
        treeFilter[optionKey] = { applyParent: false, value: reqData };
      }
    });
  }

  return { listFilter, sectionFilter, treeFilter };
};
